import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import ModalActions from 'reducers/Modal';
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList';
import PlayQueueActions from 'reducers/PlayQueue';
import NotificationActions from 'reducers/Notification';
import { thunks as ReleaseThunks } from 'reducers/Releases';
import { ListButton, RoundShadowButton } from 'components';
import { NotificationType } from 'constants/enums';
import { playNextLogo, playAtEndLogo } from 'images';
import { trackEvent } from 'utils/segmentService';

const mapStateToProps = ({ playList }) => ({
	playlists: playList.playlistAbstracts
});

// rearrange the content in the dropdown list when pressing the + add to playlist.

@connect(mapStateToProps)
class AddToPlaylistTransformer extends React.Component {
	static propTypes = {
		previousOptions: PropTypes.element,
		afterOptions: PropTypes.element,
		showPlayQueueOptions: PropTypes.bool,
		release: PropTypes.object,
		song: PropTypes.object,
		playlists: PropTypes.arrayOf(PropTypes.object),
		dispatch: PropTypes.func,
		setOnOff: PropTypes.func,
		t: PropTypes.func,
		isOpen: PropTypes.bool,
		excludePlaylist: PropTypes.object,
		name: PropTypes.string,
		trackingSource: PropTypes.string
	}

	constructor(props) {
		super(props);
		const { isOpen } = props;
		this.state = { isOpen };
	}

	fetchSongsIfNeeded = () => {
		const { dispatch } = this.props;
		return new Promise((resolve, reject) => {
			const { release } = this.props;
			if (release !== undefined) {
				if (release.songs === undefined) {
					dispatch(
						ReleaseThunks.getDetail(
							release.releaseid,
							result => {
								resolve(result);
							},
							() => {
								reject();
							}
						)
					);
				} else {
					resolve(release);
				}
			} else {
				resolve();
			}
		});
	}

	handleAddToPlaylist = playlist => {
		const { dispatch, release, song, setOnOff, trackingSource } = this.props;
		this.fetchSongsIfNeeded()
			.then(result => {
				const releaseWithSongs =
					result && release
						? {
								...release,
								songs: result.songs || release.songs || []
						  }
						: release;

				const trackIds = this.getTrackIds(releaseWithSongs);

				if (trackingSource) {
					const properties = {
						source: trackingSource.toLowerCase().replace(/_/g, '_'),
						type: 'add_to_playlist',
						playlist_id: String(playlist.playlistid),
						playlist_name: playlist.name || '',
						track_count: String(trackIds.length)
					};

					if (releaseWithSongs) {
						properties.release_id = String(releaseWithSongs.releaseid);
						properties.artist = releaseWithSongs.artist || '';
						properties.title = releaseWithSongs.title || '';
					} else if (song) {
						properties.id = String(song.trackid);
						properties.artist = song.artist || '';
						properties.title = song.title || '';
					}

					trackEvent('Click', properties);
				}

				dispatch(
					PlaylistThunks.addTracks(
						playlist.playlistid,
						trackIds,
						() => {
							if (setOnOff) {
								setOnOff(false);
							}
							dispatch(
								NotificationActions.broadcastMessage(
									{
										songTitle: this.getTrackNames(),
										playlistTitle: playlist.name
									},
									NotificationType.addedToPlaylist
								)
							);
						}
					)
				);
			})
			.catch(e => console.warn(e));
	}

	handleClickAddButton = () => this.setState({ isOpen: true });

	handlePlayNext = () => {
		const { dispatch, song, release } = this.props;

		if (release !== undefined && release !== null) {
			this.fetchSongsIfNeeded()
				.then(result => {
					if (result && release) {
						const releaseWithSongs = {
							...release,
							songs: result.songs || release.songs || []
						};
						dispatch(PlayQueueActions.insertItemNext(releaseWithSongs));
					}
				})
				.catch();
		} else if (song !== undefined && song !== null) {
			dispatch(PlayQueueActions.insertItemNext(song));
		}
	}

	handlePlayAtTheBottom = () => {
		const { dispatch, song, release } = this.props;
		if (release !== undefined && release !== null) {
			this.fetchSongsIfNeeded()
				.then(result => {
					if (result && release) {
						const releaseWithSongs = {
							...release,
							songs: result.songs || release.songs || []
						};
						dispatch(PlayQueueActions.insertItemAtBottom(releaseWithSongs));
					}
				})
				.catch();
		} else if (song !== undefined && song !== null) {
			dispatch(PlayQueueActions.insertItemAtBottom(song));
		}
	}

	getTrackIds = releaseOverride => {
		const { release, song } = this.props;
		const targetRelease = releaseOverride || release;
		if (targetRelease !== undefined) {
			return (targetRelease.songs || [])
				.filter(s => s.assettype == 'audio')
				.map(item => item.trackid);
		} else if (song !== undefined && song.assettype == 'audio') {
			return [song.trackid];
		} else {
			return [];
		}
	}

	getTrackNames = () => {
		const { release, song } = this.props;
		if (release !== undefined) {
			return `${release.artist} - ${release.title}`;
		} else if (song !== undefined) {
			return song.artist + ' - ' + song.title;
		} else {
			return '';
		}
	}

	renderPlayNextBtn = () => {
		const { t } = this.props;

		return (
			<div className="flex flex-row items-center justify-start w-full">
				<RoundShadowButton
					removeTextComponent
					icon={playNextLogo}
					cicleWidthClass="w-4"
					imageWidthClass="w-2" />
				<p className="text-xs px-2">
					{t('general.play-next')}
				</p>
			</div>
		);
	}

	renderPlayAtEndBtn = () => {
		const { t } = this.props;

		return (
			<div className="flex flex-row items-center justify-start w-full">
				<RoundShadowButton
					removeTextComponent
					icon={playAtEndLogo}
					cicleWidthClass="w-4"
					imageWidthClass="w-2" />
				<p className="text-xs px-2">
					{t('general.play-later')}
				</p>
			</div>
		);
	};

	renderPlayQueueOptions = () => (
		<>
			<ListButton
				render={this.renderPlayNextBtn}
				onClick={this.handlePlayNext} />
			<ListButton
				render={this.renderPlayAtEndBtn}
				onClick={this.handlePlayAtTheBottom} />
		</>
	);

	render() {
		const { isOpen } = this.state;
		const {
			dispatch,
			t,
			previousOptions,
			afterOptions,
			playlists,
			excludePlaylist,
			setOnOff,
			name,
			release,
			showPlayQueueOptions
		} = this.props;
		if (isOpen) {
			return (
				<>
					{!showPlayQueueOptions && previousOptions}
					{!showPlayQueueOptions && afterOptions}
					{showPlayQueueOptions && this.renderPlayQueueOptions()}
					{(previousOptions ||
						afterOptions ||
						showPlayQueueOptions) && (
						<div
							className=" w-full bg-gray-250 bg-gray-200"
							style={{ height: 1 }} />
					)}
					{playlists.map(playlist => {
						if (
							playlist.playlistid !==
							(excludePlaylist && excludePlaylist.playlistid)
						) {
							return (
								<ListButton
									key={playlist.playlistid}
									name={'+ ' + playlist.name}
									onClick={() => this.handleAddToPlaylist(playlist)} />
							);
						} else {
							return null;
						}
					})}
					<button
						type="button"
							className="bg-lightNavy px-4 py-2 text-white text-xs focus:outline-none w-full"
							onClick={e => {
								e.stopPropagation();
								this.fetchSongsIfNeeded()
									.then(result => {
										const releaseWithSongs =
											result && release
												? {
														...release,
														songs: result.songs || release.songs || []
												  }
												: release;
										dispatch(
											PlaylistActions.setSongsToAdd(
												this.getTrackIds(releaseWithSongs)
											)
										);
						dispatch(
							ModalActions.openPlaylistModal('release_modal')
						);
										if (setOnOff) {
											setOnOff(false);
										}
									});
							}}>
							{t('playlist.create-new-playlist')}
						</button>
				</>
			);
		} else {
			return (
				<>
					{previousOptions}
					<ListButton
						name={
							name
								? name
								: excludePlaylist
									? t('playlist.add-to-another-playlist')
									: '+ ' + t('general.addto')
						}
						onClick={this.handleClickAddButton} />
					{afterOptions}
				</>
			);
		}
	}
}


export default withTranslation()(AddToPlaylistTransformer);
